import {
  ArrowCounterClockwiseIcon,
  CaretDownIcon,
  CheckCircleIcon,
  PencilSimple,
  Tag,
} from "@phosphor-icons/react"
import { Button, Commandbar } from "~/components/design-system/components"
import { reviewState, tallyReviews } from "~/components/demand-2/shared/reviewStatus"
import { useVariantReviews } from "~/components/demand-2/shared/useVariantReviews"
import { ReviewStatusMenu } from "~/components/demand-2/shared/VariantReviewStatus"
import MultiEditDialog from "~/components/planning/MultiEditDialog/MultiEditDialog"
import { EditSkuTagsDialog } from "~/components/planning/Tags/EditSkuTagsDialog"
import { useVariantsByIds } from "~/hooks/useVariants"
import { getCommonVendors } from "~/utils/vendorUtils"
import { type IVariantSummary } from "~/hooks/useVariantTableData"

type Demand2CommandBarProps = {
  selectedVariantIds: string[]
  selectedVariantCount: number
  clearSelectedIds: () => void
}

function ReviewStatusAction({ variantIds }: { variantIds: string[] }) {
  const { reviews, setReviewed } = useVariantReviews()
  const state = reviewState(tallyReviews(variantIds, reviews))

  if (state === "mixed") {
    return (
      <ReviewStatusMenu variantIds={variantIds} reviews={reviews} onSet={setReviewed} asChild>
        <Button size="xs" variant="neutral">
          <CheckCircleIcon />
          Change status
          <CaretDownIcon />
        </Button>
      </ReviewStatusMenu>
    )
  }
  const markReviewed = state === "none"
  return (
    <Button
      size="xs"
      variant="neutral"
      requires="edit"
      onClick={() => setReviewed(variantIds, markReviewed)}
    >
      {markReviewed ? <CheckCircleIcon /> : <ArrowCounterClockwiseIcon />}
      {markReviewed ? "Mark as reviewed" : "Mark as pending"}
    </Button>
  )
}

/**
 * Bulk actions for the Demand 2.0 tables, driven by the checkbox selection. No "Inspect" action:
 * the table mirrors the selection into the inspector as it changes. Triggers are `size="xs"` for
 * the Commandbar; no `disabled` on them, since the `requires="edit"` gate lives on each dialog's
 * `DialogTrigger` (or on the button itself for the direct review action).
 */
export function Demand2CommandBar({
  selectedVariantIds,
  selectedVariantCount,
  clearSelectedIds,
}: Demand2CommandBarProps) {
  const variants = useVariantsByIds(selectedVariantIds)
  const skuIds = variants.flatMap((v: IVariantSummary) => v.skuIds)
  const commonVendors = getCommonVendors(variants)

  if (selectedVariantCount === 0) return null

  return (
    <Commandbar count={selectedVariantCount} onClear={clearSelectedIds}>
      <ReviewStatusAction variantIds={selectedVariantIds} />
      <MultiEditDialog
        selectedSkuIds={skuIds}
        selectedVariantCount={selectedVariantCount}
        resetSelectedProductIds={clearSelectedIds}
        commonVendors={commonVendors}
        customTrigger={
          <Button size="xs" variant="neutral">
            <PencilSimple />
            Settings
          </Button>
        }
      />
      <EditSkuTagsDialog
        variantIds={selectedVariantIds}
        customTrigger={
          <Button size="xs" variant="neutral">
            <Tag />
            Tags
          </Button>
        }
      />
    </Commandbar>
  )
}
